import { readDashboardSessionForRequest } from "@dashboard/lib/dashboard-session-server"
import { DashboardProviders } from "@dashboard/routes/DashboardProviders"
import type { AuthSession } from "@playbrain/auth/dashboard"
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"
import { dashboardAppHostRedirectUrl } from "@utils/dashboard-host"
import { currentRequestHostname } from "@utils/dashboard-host-runtime"

export const Route = createFileRoute("/dashboard")({
  component: DashboardLayoutRoute,
  beforeLoad: async ({ location }) => {
    const redirectHref = dashboardAppHostRedirectUrl(await currentRequestHostname(), location.href)
    if (redirectHref) throw redirect({ href: redirectHref })
  },
  /**
   * Resolves the session before the first byte of HTML, so the dashboard's
   * permission-filtered chrome paints real instead of as a skeleton. Loader
   * rather than `beforeLoad` so a slow users API delays the shell, not the
   * redirect decision above it.
   */
  loader: async () => ({ serializedSession: await readDashboardSessionForRequest() }),
})

/**
 * Mounts the dashboard's providers once for every `/dashboard/*` page.
 *
 * Home and the sections are separate routes, and each used to mount its own
 * copy of the stack, so moving between them tore down the theme, the toast
 * host and the session gate and built them again — re-running the auth check
 * on a session that had not changed. This route stays mounted across all of
 * them, so now only the content below the Outlet swaps.
 *
 * The surfaces below still declare their own provider boundary, which becomes
 * a pass-through under this one. That keeps each renderable on its own, and
 * keeps the Contract Maker deployment working, since it has no layout route.
 */
function DashboardLayoutRoute() {
  const { serializedSession } = Route.useLoaderData()
  const initialSession = parseInitialSession(serializedSession)

  return (
    <DashboardProviders initialSession={initialSession}>
      <Outlet />
    </DashboardProviders>
  )
}

/** The loader hands the session across as JSON; unreadable input means none. */
function parseInitialSession(serialized: string | null): AuthSession | null {
  if (!serialized) return null
  try {
    return JSON.parse(serialized) as AuthSession
  } catch {
    return null
  }
}
